import { useMemo } from "react";
import { cn } from "@/lib/utils";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Input } from "./ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui/table";
import {
  FileText,
  Euro,
  Loader2,
  RefreshCw,
  Search,
  Filter,
  Eye,
  ChevronLeft,
  ChevronRight,
  Trash2,
  CheckCircle2,
  Clock,
  Clock3,
  Ban,
  ArrowDownUp,
  Inbox,
} from "lucide-react";
import { ficEiStatusLabel, ficEiStatusEliminabile } from "../utils/fatturazioneSdi";

const STATO_PAGAMENTO_ATTIVE_UI = {
  pagata: { label: "Pagata", className: "bg-emerald-50 text-emerald-800", Icon: CheckCircle2 },
  parziale: { label: "Parziale", className: "bg-violet-50 text-violet-800", Icon: Clock3 },
  da_incassare: { label: "Da incassare", className: "bg-orange-50 text-orange-800", Icon: Clock },
  scaduta: { label: "Scaduta", className: "bg-red-50 text-red-800", Icon: Ban },
};

function resolveStatoPagamentoAttiva(it) {
  const sp = String(it?.stato_pagamento || "non_pagata").toLowerCase();
  if (sp === "pagata") return "pagata";
  if (sp === "parziale") return "parziale";
  if (sp === "scaduta") return "scaduta";
  const scad = String(it?.data_scadenza || "").slice(0, 10);
  if (scad && /^\d{4}-\d{2}-\d{2}$/.test(scad)) {
    const today = new Date();
    const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
    if (scad < todayStr) return "scaduta";
  }
  return "da_incassare";
}

function StatoPagamentoAttivaBadge({ it }) {
  const key = resolveStatoPagamentoAttiva(it);
  const cfg = STATO_PAGAMENTO_ATTIVE_UI[key] || STATO_PAGAMENTO_ATTIVE_UI.da_incassare;
  const Icon = cfg.Icon;
  return (
    <span className={cn("inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium", cfg.className)}>
      <Icon className="w-3 h-3 shrink-0" />
      {cfg.label}
    </span>
  );
}

function AttiveKpiCard({ icon: Icon, iconClass, title, value, sub, valueClass }) {
  return (
    <Card className="card-base">
      <CardContent className="p-4 flex items-start gap-3">
        <span className={cn("inline-flex h-9 w-9 items-center justify-center rounded-lg shrink-0", iconClass)}>
          <Icon className="w-4 h-4" />
        </span>
        <div className="min-w-0">
          <p className="text-xs text-slate-500">{title}</p>
          <p className={cn("text-lg font-semibold tabular-nums", valueClass || "text-slate-900")}>{value}</p>
          {sub && <p className="text-xs text-slate-500 mt-0.5">{sub}</p>}
        </div>
      </CardContent>
    </Card>
  );
}

function formatDataIt(data) {
  if (!data) return "—";
  const s = String(data).slice(0, 10);
  const [y, m, d] = s.split("-");
  if (!y || !m || !d) return s;
  return `${d}/${m}/${y}`;
}

export default function FatturazioneFattureAttiveTab({
  hasProviderConfig,
  isFic,
  attiveFiltroMese,
  setAttiveFiltroMese,
  attiveMesiOptions,
  formatMese,
  formatEuro,
  attiveItems,
  attiveLoading,
  attiveTotal,
  attiveImportoTotale,
  kpiConfrontoMensile,
  attivePage,
  attiveLastPage,
  setAttivePage,
  attiveSearchQuery,
  setAttiveSearchQuery,
  loadFattureAttive,
  handleSyncFicNoteCredito,
  syncFicNcLoading,
  handleSyncPagamentiFic,
  syncPagamentiLoading,
  handleOpenAttivaPdf,
  downloadingAttivaPdf,
  setDeleteAttivaTarget,
}) {
  const filteredItems = useMemo(() => {
    const q = attiveSearchQuery.trim().toLowerCase();
    if (!q) return attiveItems;
    return attiveItems.filter((it) => {
      const hay = [
        it.numero,
        it.ospite,
        it.destinatario,
        it.destinatario_cf,
        it.data,
        String(it.totale ?? ""),
        it.ei_status,
        it.tipo_documento,
        it.fattura_riferimento_numero,
        it.stato_pagamento,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return hay.includes(q);
    });
  }, [attiveItems, attiveSearchQuery]);

  const importoTotalePagina = useMemo(
    () => attiveItems.reduce((a, it) => a + (Number(it.totale) || 0), 0),
    [attiveItems],
  );
  const importoTotale = attiveImportoTotale ?? importoTotalePagina;

  const collegateCount = useMemo(
    () => attiveItems.filter((it) => it.gia_collegata).length,
    [attiveItems],
  );

  const daIncassareCount = useMemo(
    () => attiveItems.filter((it) => resolveStatoPagamentoAttiva(it) !== "pagata").length,
    [attiveItems],
  );

  if (!hasProviderConfig || !isFic) {
    return (
      <Card className="card-base">
        <CardContent className="p-6 text-sm text-slate-500">
          Configura Fatture in Cloud in Impostazioni per visualizzare le fatture emesse sul provider.
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">Fatture attive</h2>
          <p className="text-sm text-slate-500 mt-0.5">
            Fatture emesse su Fatture in Cloud per gli ospiti della struttura selezionata. Lo stato
            pagamento riflette i dati FIC (TSPay / riconciliazione bancaria).
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleSyncPagamentiFic}
            disabled={attiveLoading || syncPagamentiLoading}
          >
            {syncPagamentiLoading ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Euro className="w-4 h-4 mr-2" />
            )}
            Sync pagamenti
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleSyncFicNoteCredito}
            disabled={attiveLoading || syncFicNcLoading}
          >
            {syncFicNcLoading ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <FileText className="w-4 h-4 mr-2" />
            )}
            Sync note credito
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => loadFattureAttive(attivePage)}
            disabled={attiveLoading}
          >
            {attiveLoading ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4 mr-2" />
            )}
            Aggiorna
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
        <AttiveKpiCard
          icon={FileText}
          iconClass="bg-sky-100 text-sky-700"
          title="Fatture nel mese"
          value={attiveLoading ? "…" : attiveTotal}
          sub={formatMese(attiveFiltroMese)}
        />
        <AttiveKpiCard
          icon={Euro}
          iconClass="bg-emerald-100 text-emerald-700"
          title="Attive del mese"
          value={attiveLoading ? "…" : formatEuro(importoTotale)}
          sub="Ricavi FIC del mese"
        />
        <AttiveKpiCard
          icon={Inbox}
          iconClass="bg-blue-100 text-blue-700"
          title="Passive del mese"
          value={
            kpiConfrontoMensile?.loading && !attiveLoading
              ? "…"
              : formatEuro(kpiConfrontoMensile?.passive?.importo ?? 0)
          }
          sub={`${kpiConfrontoMensile?.passive?.count ?? 0} fatture · costi fornitori`}
        />
        <AttiveKpiCard
          icon={ArrowDownUp}
          iconClass={
            (kpiConfrontoMensile?.differenza ?? 0) >= 0
              ? "bg-emerald-100 text-emerald-700"
              : "bg-red-100 text-red-700"
          }
          title="Differenza (attive − passive)"
          value={
            kpiConfrontoMensile?.loading && !attiveLoading
              ? "…"
              : formatEuro(kpiConfrontoMensile?.differenza ?? 0)
          }
          valueClass={
            (kpiConfrontoMensile?.differenza ?? 0) >= 0 ? "text-emerald-700" : "text-red-700"
          }
          sub="Indicatore di controllo (non è utile fiscale)"
        />
        <AttiveKpiCard
          icon={FileText}
          iconClass="bg-violet-100 text-violet-700"
          title="Già collegate in Affidya"
          value={attiveLoading ? "…" : collegateCount}
          sub={
            attiveLoading
              ? undefined
              : `${daIncassareCount} da incassare in pagina · ${attiveItems.length} in pagina`
          }
        />
      </div>

      <Card className="card-base overflow-hidden">
        <div className="flex flex-col gap-3 p-4 border-b border-slate-100 bg-slate-50/50">
          <div className="flex flex-col lg:flex-row lg:items-center gap-3">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                className="pl-9 bg-white"
                placeholder="Cerca destinatario, numero, importo…"
                value={attiveSearchQuery}
                onChange={(e) => setAttiveSearchQuery(e.target.value)}
              />
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Select value={attiveFiltroMese} onValueChange={setAttiveFiltroMese}>
                <SelectTrigger className="w-[160px] bg-white h-9 text-sm">
                  <SelectValue placeholder="Mese" />
                </SelectTrigger>
                <SelectContent>
                  {attiveMesiOptions.map((m) => (
                    <SelectItem key={m} value={m}>
                      {formatMese(m)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-9"
                onClick={() => loadFattureAttive(1)}
                disabled={attiveLoading}
              >
                <Filter className="w-3.5 h-3.5 mr-1.5" />
                Applica
              </Button>
            </div>
          </div>
        </div>

        {attiveLoading && !attiveItems.length ? (
          <div className="py-16 text-center text-slate-500">
            <Loader2 className="w-8 h-8 animate-spin mx-auto mb-2" />
            Caricamento fatture da Fatture in Cloud…
          </div>
        ) : filteredItems.length === 0 ? (
          <p className="text-sm text-slate-500 py-12 text-center">
            Nessuna fattura FIC per gli ospiti di questa struttura in {formatMese(attiveFiltroMese)}.
          </p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="text-slate-500 font-medium">Numero</TableHead>
                <TableHead className="text-slate-500 font-medium">Tipo</TableHead>
                <TableHead className="text-slate-500 font-medium">Data</TableHead>
                <TableHead className="text-slate-500 font-medium">Ospite</TableHead>
                <TableHead className="text-slate-500 font-medium">Destinatario</TableHead>
                <TableHead className="text-slate-500 font-medium">CF</TableHead>
                <TableHead className="text-slate-500 font-medium text-right">Importo</TableHead>
                <TableHead className="text-slate-500 font-medium">Pagamento</TableHead>
                <TableHead className="text-slate-500 font-medium">Stato SDI</TableHead>
                <TableHead className="text-slate-500 font-medium">Affidya</TableHead>
                <TableHead className="text-slate-500 font-medium text-right">Azioni</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredItems.map((it) => {
                const pdfKey = `${it.fic_document_id}:pdf`;
                return (
                  <TableRow key={it.fic_document_id}>
                    <TableCell className="font-medium tabular-nums">{it.numero ?? "—"}</TableCell>
                    <TableCell>
                      {it.tipo_documento === "nota_credito" ? (
                        <Badge className="text-[10px] bg-purple-100 text-purple-800">Nota credito</Badge>
                      ) : (
                        <Badge variant="outline" className="text-[10px] text-slate-600">Fattura</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-slate-600">{formatDataIt(it.data)}</TableCell>
                    <TableCell className="max-w-[140px] truncate font-medium text-slate-800">
                      {it.ospite || "—"}
                    </TableCell>
                    <TableCell className="max-w-[180px] truncate">{it.destinatario || "—"}</TableCell>
                    <TableCell className="text-xs text-slate-500 font-mono">{it.destinatario_cf || "—"}</TableCell>
                    <TableCell className="text-right font-semibold tabular-nums">
                      {formatEuro(it.totale ?? 0)}
                    </TableCell>
                    <TableCell>
                      <StatoPagamentoAttivaBadge it={it} />
                    </TableCell>
                    <TableCell>
                      {it.ei_status ? (
                        <Badge variant="outline" className="text-[10px]">
                          {ficEiStatusLabel(it.ei_status)}
                        </Badge>
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {it.gia_collegata ? (
                        <Badge className="text-[10px] bg-emerald-100 text-emerald-800">Collegata</Badge>
                      ) : (
                        <Badge variant="outline" className="text-[10px] text-slate-500">—</Badge>
                      )}
                      {it.tipo_documento === "nota_credito" && it.fattura_riferimento_numero && (
                        <Badge variant="outline" className="ml-1 text-[10px] text-purple-700">
                          Rif. {it.fattura_riferimento_numero}
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="h-8 text-xs"
                          disabled={downloadingAttivaPdf === pdfKey}
                          onClick={() => handleOpenAttivaPdf(it)}
                        >
                          {downloadingAttivaPdf === pdfKey ? (
                            <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
                          ) : (
                            <Eye className="w-3.5 h-3.5 mr-1" />
                          )}
                          PDF
                        </Button>
                        {ficEiStatusEliminabile(it.ei_status) && (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="h-8 text-xs text-red-700 border-red-200 hover:bg-red-50"
                            onClick={() => setDeleteAttivaTarget(it)}
                          >
                            <Trash2 className="w-3.5 h-3.5 mr-1" />
                            Elimina su FIC
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}

        {attiveLastPage > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100 text-sm text-slate-600">
            <span>
              Pagina {attivePage} di {attiveLastPage} · {attiveTotal} fatture
            </span>
            <div className="flex gap-1">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-8"
                disabled={attivePage <= 1 || attiveLoading}
                onClick={() => loadFattureAttive(attivePage - 1)}
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-8"
                disabled={attivePage >= attiveLastPage || attiveLoading}
                onClick={() => loadFattureAttive(attivePage + 1)}
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
